"use client"

import { useEffect, useRef, useState } from "react"

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel"
import { Button } from "@/components/ui/button"
import { ChevronsRight, Star } from "lucide-react"

type Testimonial = {
  id: number
  name: string
  initials: string
  designation: string
  rating: number
  message: string
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Rohan Patil",
    initials: "RP",
    designation: "Parent",
    rating: 5,
    message:
      "Great staff and very cooperative environment. Highly recommended!",
  },
  {
    id: 2,
    name: "Sudesh Thakur",
    initials: "ST",
    designation: "Parent",
    rating: 5,
    message:
      "Great college. Well equipped classrooms. Staff is also helpful. The bldg is a bit confusing for new people. Coordinator is very helpful and tries to meet most of parents queries. Kudos to the team 👏",
  },
  {
    id: 3,
    name: "KAVITA R",
    initials: "KR",
    designation: "Parent",
    rating: 5,
    message:
      "The infrastructure of the school is very good. ",
  },
  {
    id: 4,
    name: "GRENITA",
    initials: "G",
    designation: "Alumni",
    rating: 5,
    message:
      "An educational institution that nurtures an individual into a STAR!!",
  },
  {
    id: 5,
    name: "JAGDISH MOOLYA",
    initials: "JM",
    designation: "Guardian",
    rating: 4,
    message:
      "Nice school with beautiful campus And Both teaching and non-teaching staffs are very co-operative Best CBSE School In saphale.",
  },
]

export function TestimonialSection() {
  const [api, setApi] = useState<CarouselApi | null>(null)
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)
  const pauseRef = useRef(false)

  useEffect(() => {
    if (!api) return

    const updateState = () => {
      setCurrent(api.selectedScrollSnap())
      setCount(api.scrollSnapList().length)
    }

    updateState()
    api.on("select", updateState)
    api.on("reInit", updateState)

    return () => {
      api.off("select", updateState)
      api.off("reInit", updateState)
    }
  }, [api])

  useEffect(() => {
    if (!api) return

    const autoplay = setInterval(() => {
      if (pauseRef.current) return

      if (api.canScrollNext()) {
        api.scrollNext()
      } else {
        api.scrollTo(0)
      }
    }, 5000)

    return () => {
      clearInterval(autoplay)
    }
  }, [api])

  return (
    <section className="relative overflow-hidden py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white via-red-50/60 to-white">
      {/* dotted background layers */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle,rgba(248,113,113,0.12)_1px,transparent_1px)] [background-size:22px_22px]" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-70 bg-[radial-gradient(circle,rgba(185,28,28,0.10)_1px,transparent_1px)] [background-size:12px_12px]" />
      {/* animated subtle dotted drift */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-40 bg-[radial-gradient(circle,rgba(185,28,28,0.10)_1px,transparent_1px)] [background-size:16px_16px] animate-[dotDrift_30s_linear_infinite]" />

      {/* floating bubbles */}
      <span className="pointer-events-none absolute -left-10 top-10 h-40 w-40 rounded-full bg-[#b91c1c]/10 blur-2xl animate-[floatUp_14s_ease-in-out_infinite]" />
      <span className="pointer-events-none absolute right-[-30px] top-1/3 h-56 w-56 rounded-full bg-[#f87171]/10 blur-2xl animate-[floatUp_18s_ease-in-out_infinite]" />
      <span className="pointer-events-none absolute left-1/3 bottom-[-60px] h-48 w-48 rounded-full bg-[#dc2626]/10 blur-2xl animate-[floatUp_20s_ease-in-out_infinite]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-[#7a1111]">
            Client Testimonials
          </h3>
          <p className="mt-4 text-lg text-[#5f2530]/85">
            Genuine
            <span className="relative ml-2 inline-flex items-center">
              <span className="relative z-10 font-semibold text-[#9f1c1c]">Reviews</span>
              <span className="absolute left-1/2 top-full h-[8px] w-[110%] -translate-x-1/2 rounded-full bg-gradient-to-r from-[#fbb4b4] via-[#f87171] to-[#b91c1c]" />
            </span>
          </p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => {
            pauseRef.current = true
          }}
          onMouseLeave={() => {
            pauseRef.current = false
          }}
          onTouchStart={() => {
            pauseRef.current = true
          }}
          onTouchEnd={() => {
            pauseRef.current = false
          }}
        >
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: true }}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {testimonials.map((item) => (
                <CarouselItem
                  key={item.id}
                  className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <article className="relative h-full rounded-[32px] border border-red-100/70 bg-white px-6 py-10 sm:px-9  flex flex-col gap-6">
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#b91c1c] to-[#dc2626] text-white text-2xl font-bold shadow-[0px_12px_30px_rgba(185,28,28,0.35)]">
                        {item.initials}
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-[#7a1111]">
                          {item.name}
                        </h4>
                        <p className="text-sm font-medium uppercase tracking-[0.24em] text-[#5f2530]/70">
                          {item.designation}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-[6px] text-[#f97316]">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Star
                          key={index}
                          size={20}
                          className={index < item.rating ? "fill-current" : "opacity-30"}
                        />
                      ))}
                    </div>

                    <p className="text-base leading-relaxed text-[#5f2530]/85">
                      {item.message}
                    </p>

                    <span className="pointer-events-none absolute inset-x-8 bottom-[18px] h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#dc2626]/30 to-transparent" />
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="absolute left-[-2.5rem] top-1/2 hidden h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white text-[#b91c1c] shadow-[0px_20px_40px_rgba(185,28,28,0.15)] transition hover:shadow-[0px_24px_48px_rgba(185,28,28,0.2)] md:flex after:absolute after:bottom-2 after:right-2 after:h-3 after:w-3 after:rounded-full after:bg-gradient-to-tr after:from-[#b91c1c] after:to-[#f97373]" />
            <CarouselNext className="absolute right-[-2.5rem] top-1/2 hidden h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white text-[#b91c1c] shadow-[0px_20px_40px_rgba(185,28,28,0.15)] transition hover:shadow-[0px_24px_48px_rgba(185,28,28,0.2)] md:flex after:absolute after:bottom-2 after:right-2 after:h-3 after:w-3 after:rounded-full after:bg-gradient-to-tr after:from-[#b91c1c] after:to-[#f97373]" />
          </Carousel>

          <div className="mt-10 flex flex-col items-center gap-6">
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.4em] text-[#5f2530]/85">
              {String(current + 1).padStart(2, "0")} <span className="text-[#f87171]">••</span> {String(count).padStart(2, "0")}
            </div>
            <div className="flex items-center gap-3">
              {Array.from({ length: count }).map((_, index) => (
                <span
                  key={index}
                  className={
                    index === current
                      ? "flex size-5 items-center justify-center rounded-full border border-[#b91c1c] bg-white shadow-[0px_6px_12px_rgba(185,28,28,0.22)]"
                      : "size-3 rounded-full bg-[#f3b4b4]"
                  }
                />
              ))}
            </div>

            <Button
              variant="ghost"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-[#b91c1c] via-[#dc2626] to-[#f87171] px-8 py-4 text-white shadow-[0px_18px_40px_rgba(185,28,28,0.3)] transition hover:shadow-[0px_24px_50px_rgba(185,28,28,0.35)]"
            >
              <span className="flex size-8 items-center justify-center rounded-full bg-white/15 text-white transition-transform group-hover:translate-x-1">
                <ChevronsRight size={18} />
              </span>
              <a className="text-base font-semibold tracking-wide" href="https://maps.app.goo.gl/P2TW2a5CnwLtXJF96" target="_blank" rel="noopener noreferrer">More Reviews on Google</a>
            </Button>
          </div>
        </div>
      </div>

      {/* background animations */}
      <style jsx>{`
        @keyframes floatUp {
          0% { transform: translateY(20px) translateX(0) scale(1); }
          50% { transform: translateY(-20px) translateX(10px) scale(1.05); }
          100% { transform: translateY(20px) translateX(0) scale(1); }
        }
        @keyframes dotDrift {
          0% { transform: translateX(0); }
          100% { transform: translateX(-60px); }
        }
      `}</style>
    </section>
  )
}

